export type AgreementAutoCloseGate = {
  /** The record is a Lead and its funnel opted in. */
  autoCloseLeadActive: boolean
  /** Lead auto-close is active and no opportunity-required fields are outstanding. Drives the green banner. */
  showAutoCloseLeadBanner: boolean
  /** Lead auto-close is active but required fields are outstanding. Drives the red banner. */
  showMissingFieldsBanner: boolean
  /** The record is an Opportunity whose funnel opted in. Drives the "Closed Won Queue" paragraph. */
  showOppAutoCloseMessage: boolean
  missingFieldIds: number[]
  missingFieldCount: number
}

export type AgreementAutoCloseGateInput = {
  isLead: boolean
  /** The record's funnel `autoCloseWinLeads`. */
  autoCloseWinLeads: boolean
  /** The record's funnel `autoCloseWinOpportunities`. */
  autoCloseWinOpportunities: boolean
  /** `undefined` while the missing-fields query is loading, disabled, or failed. */
  missingFieldIds: number[] | null | undefined
}

const EMPTY_MISSING_FIELD_IDS: number[] = []

/**
 * Picks the single auto-close message the Create Agreement page may show.
 *
 * A funnel can have both auto-close settings on, and `autoCloseWinOpportunities`
 * defaults to true server-side, so entity type is the discriminator: an Opportunity
 * only ever gets the "Closed Won Queue" paragraph, a Lead only ever gets its own
 * green/red banner. The card-on-file sentence is independent of all of this.
 */
export function resolveAgreementAutoClose(
  input: AgreementAutoCloseGateInput,
): AgreementAutoCloseGate {
  const {
    isLead,
    autoCloseWinLeads,
    autoCloseWinOpportunities,
    missingFieldIds,
  } = input

  const autoCloseLeadActive = isLead && autoCloseWinLeads

  const hasMissingFieldData = Array.isArray(missingFieldIds)

  return {
    autoCloseLeadActive,
    showAutoCloseLeadBanner:
      autoCloseLeadActive &&
      hasMissingFieldData &&
      missingFieldIds.length === 0,
    showMissingFieldsBanner:
      autoCloseLeadActive && hasMissingFieldData && missingFieldIds.length > 0,
    showOppAutoCloseMessage: !isLead && autoCloseWinOpportunities,
    missingFieldIds: hasMissingFieldData
      ? missingFieldIds
      : EMPTY_MISSING_FIELD_IDS,
    missingFieldCount: hasMissingFieldData ? missingFieldIds.length : 0,
  }
}

/** The subset of a Lead/Opportunity detail record the funnel lookup needs. */
export type AutoCloseEntityDetail = {
  id: number
  salesFunnelId: number
}

/**
 * Picks the funnel that decides the message, or `undefined` when we can't trust it yet.
 *
 * The Lead and Opportunity detail records live in shared Redux slices that hold one
 * record at a time, and neither `useGetLeadDetailOnMount` nor
 * `useGetOpportunityDetailOnMount` clears the slice before fetching. Opening this
 * dialog from a surface that doesn't own the detail page — the Manage Agreements
 * widget or the map view drawer — therefore sees the previously viewed record until
 * the fetch lands. Matching on id keeps a stale record from briefly choosing the
 * wrong message.
 */
export function resolveAutoCloseFunnelId(
  entityDetail: AutoCloseEntityDetail | null | undefined,
  entityId: number,
): number | undefined {
  return entityDetail?.id === entityId ? entityDetail.salesFunnelId : undefined
}
